import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie'; // TODO: (KPL-import useShoppingBagContext from './myFutureCode';
import { formatMoneyForLocale, useI18n } from '@stitch-fix/i18n';
import {
  Box,
  Text,
  Stack,
  Toast,
  IconError16,
  IconGiftCards16,
  IconLockLocked16,
  IconLockLocked24,
  IconGiftCards24,
  Banner,
  Divider,
  useScreenSize,
} from '@stitch-fix/mode-react';
import PropTypes from 'prop-types';
import ErrorBoundary from '../shared/ErrorBoundary/ErrorBoundary';
import EmptyBag from './EmptyBag';
import BagHeader from './BagHeader';
import BagFooter from './BagFooter';
import BagInventoryFailureModal from './BagInventoryFailureModal/BagInventoryFailureModal';
import {
  decrementBagCount,
  incrementBagCount,
} from './helpers/shoppingBagCount';
import redirectToSignup from '../../helpers/redirectToSignup';
import BagItem from './BagItem';
import ShippingDetails from '../ShippingDetails/ShippingDetails';
import useShoppingBagProductDetailsQuery from './useGetShoppingBagProductDetailsQuery';
import useShoppingBagVisitorQuery from './useGetShoppingBagVisitorQuery';
import CreditIncentivesBanner from '../CreditIncentivesBanner';

import './styles.module.scss';
import StickyCTA from '../shared/StickyCTA';
import {
  trackAddToBagEvent,
  trackReviewOrderClick,
  trackShoppingBagView,
  trackBagIconClick,
  trackRemoveBagItemEvent,
} from './ShoppingBag.tracking';
import getICQueryValue from '../../helpers/iconClick';
import { OUT_OF_STOCK } from '../../helpers/availability';
import formatBusinessLine from '../../helpers/businessLineFormatter';
import { sendCount } from '../../utils/sendMetrics';
import sourceType from './ShoppingBag.types';

const ShoppingBag = ({ isLoggedIn, canSeeShippingPromises }) => {
  const { locale } = useI18n();
  const [pageViewTracked, setPageViewTracked] = useState(false);
  const [bagIconClickTracked, setBagIconClickTracked] = useState(false);

  const BAG_QUANTITY_LIMIT = 100;
  const bagJson = window.localStorage?.getItem('ShoppingBag');
  const bag = bagJson ? JSON.parse(bagJson) : {};
  const [bagItems, setBagItems] = useState(bag?.items || {});
  const bagBusinessLine = bag?.businessLine;
  const skuIds = Object.keys(bagItems);

  const bagIsEmpty = skuIds.length === 0;
  const visitorId = Cookies.get('visitor_uuid'); // TODO: useShoppingBagContext();
  const [displayBagLimitToast, setDisplayBagLimitToast] = useState(false);

  const [businessLineMismatch, setBusinessLineMismatch] = useState(false);
  const [creditIncentivesEligible, setCreditIncentivesEligible] =
    useState(false);

  const [showSecurePaymentMessage, setSecurePaymentMessage] = useState(false);

  const {
    shoppingBagProductDetailsData: productData,
    shoppingBagProductDetailsLoading: loading,
    // TODO: use this -> shoppingBagProductDetailsError: error,
  } = useShoppingBagProductDetailsQuery(skuIds);

  const { shoppingBagVisitorData: visitorData } =
    useShoppingBagVisitorQuery(visitorId);

  const screenSize = useScreenSize();
  const isMobileScreenSize = screenSize === 'md' || screenSize === 'sm';

  useEffect(() => {
    const { visitor } = visitorData || {};

    if (visitor?.id) {
      const { configParams } = visitor;

      if (!configParams) {
        return;
      }

      const eligibleForExperiment = configParams.some(
        ({ name, value }) =>
          name === 'eng.kufak.freestyle.credit_incentive.test_case_1' &&
          value === 'Accessible',
      );

      if (eligibleForExperiment) {
        setCreditIncentivesEligible(true);
      }

      const enableSecurePaymentMessage = configParams.some(
        ({ name, value }) =>
          name === 'eng.kufak.shopping.bag.secure_payment_copy' &&
          value === 'enabled',
      );

      if (enableSecurePaymentMessage) {
        setSecurePaymentMessage(true);
      }
    }
  }, [visitorData]);

  useEffect(() => {
    // Clients who have signed up for Stitch Fix but not completed onboarding will see this ungated shopping bag page
    // They may or may not have a business line
    const { client } = visitorData || {};

    if (client?.businessLine && bagBusinessLine) {
      // For users with a client business line, the GraphQL API response will assign the Out of Stock availability to any sku that belongs to a different business line, regardless of the actual availablity
      // We want to hide the Out of Stock labels and display a banner that informs the user of the business line mismatch
      if (client.businessLine !== bagBusinessLine) {
        setBusinessLineMismatch(true);
      }
    }
  }, [visitorData, bagBusinessLine]);

  const products = productData?.shopProductsBySkuIds?.nodes;
  const skus = products?.map(product => product.variantBySkuId);

  useEffect(() => {
    if (!pageViewTracked) {
      if (bagIsEmpty) {
        trackShoppingBagView({ skus: [], bagItems: {} });
        setPageViewTracked(true);
      } else if (skus) {
        trackShoppingBagView({ skus, bagItems });
        setPageViewTracked(true);
      }
    }
  }, [pageViewTracked, bagIsEmpty, skus, bagItems]);

  useEffect(() => {
    const trackPreviousBagIconClick = () => {
      // Knit appends `?ic=<canonicalName>` to the link
      // The `canonicalName` is the name of the page the user was on when they clicked the bag icon in the header
      const canonicalName = getICQueryValue();

      if (!canonicalName) {
        return;
      }

      if (canonicalName === 'true') {
        trackBagIconClick('shopping_bag');
      } else {
        trackBagIconClick(canonicalName);
      }

      setBagIconClickTracked(true);
      // remove the param without reloading page or altering history
      window.history.replaceState({}, '', '/product/shopping_bag');
    };

    if (!bagIconClickTracked) {
      trackPreviousBagIconClick();
    }
  }, [bagIconClickTracked]);

  const getTotalBagQuantity = items => {
    let totalQuantity = 0;

    Object.keys(items).forEach(function (key) {
      totalQuantity += items[key].quantity;
    });

    return totalQuantity;
  };

  const updateShoppingBag = newBagItems => {
    const updatedTotalQuantity = getTotalBagQuantity(newBagItems);

    const newBag = {
      ...bag,
      items: newBagItems,
      totalQuantity: updatedTotalQuantity,
    };

    if (!newBagItems || Object.keys(newBagItems).length === 0) {
      delete newBag.businessLine;
    }

    window.localStorage.setItem('ShoppingBag', JSON.stringify(newBag));
  };

  const onRemoveButtonClick = sku => {
    const skuId = sku.id;
    const bagItem = bagItems[skuId];
    const newBagItems = { ...bagItems };

    delete newBagItems[skuId];

    updateShoppingBag(newBagItems);
    setBagItems(newBagItems);

    trackRemoveBagItemEvent({ sku, bagItem });

    decrementBagCount();
  };

  const onIncrementButtonClick = sku => {
    const skuId = sku.id;
    const newBagItems = { ...bagItems };
    const bagItem = newBagItems[skuId];

    if (getTotalBagQuantity(bagItems) < BAG_QUANTITY_LIMIT) {
      bagItem.quantity += 1;

      updateShoppingBag(newBagItems);
      setBagItems(newBagItems);

      trackAddToBagEvent({ sku, bagItem });

      incrementBagCount();
    } else {
      setDisplayBagLimitToast(true);
    }
  };

  const onDecrementButtonClick = sku => {
    const skuId = sku.id;
    const newBagItems = { ...bagItems };
    const bagItem = newBagItems[skuId];

    if (bagItem.quantity <= 1) {
      delete newBagItems[skuId];
    } else {
      bagItem.quantity -= 1;
    }

    updateShoppingBag(newBagItems);
    setBagItems(newBagItems);

    trackRemoveBagItemEvent({ sku, bagItem });

    decrementBagCount();
  };

  const renderBagItem = product => {
    const sku = product.variantBySkuId;

    return (
      <BagItem
        key={sku.id}
        product={product}
        quantity={bagItems[sku.id]?.quantity}
        subSource={bagItems[sku.id]?.sub_source}
        hidden={!skuIds.includes(sku.id)}
        decrementQuantity={() => onDecrementButtonClick(sku)}
        incrementQuantity={() => onIncrementButtonClick(sku)}
        remove={() => onRemoveButtonClick(sku)}
        showStockStatus={!businessLineMismatch}
      />
    );
  };

  const inStockSkus = () => {
    return skus
      ?.filter(sku => skuIds.includes(sku.id))
      .filter(
        sku =>
          sku.inventory.availability === 'AVAILABLE_STOCK' ||
          sku.inventory.availability === 'LIMITED_STOCK',
      );
  };

  const isAnySkuInStock = () => {
    const inStock = inStockSkus();

    return !!inStock && inStock.length > 0;
  };

  const skuQuantityExceedsAvailable = () => {
    const exceedsAvailable = skus
      ?.filter(sku => skuIds.includes(sku.id))
      .filter(sku => sku.inventory.availability !== OUT_OF_STOCK)
      .some(sku => {
        return bagItems[sku.id]?.quantity > sku.inventory.quantityAvailable;
      });

    // This may send multiple counts for the same instance
    // Because we don't expect this to happen very often or at all
    // We'll revisit if we see any occurances
    if (exceedsAvailable) {
      sendCount('shopping_bag_quantity_exceeds_quantity_available');
    }

    return exceedsAvailable;
  };

  const renderEmptyBag = () => (
    // TODO: pass in visitor context. Right now we always show the default empty bag.
    <div styleName="empty-shopping-bag-body">
      <EmptyBag navigatedFromPDP={false} navigatedFromLandingPage={false} />
    </div>
  );

  const formattedSubtotal = () => {
    if (loading || !bagItems) {
      return 'Calculating';
    }

    const totalValue = skus
      .filter(sku => Object.keys(bagItems).includes(sku.id))
      .filter(sku => sku.inventory.availability !== OUT_OF_STOCK)
      .reduce((prevValue, sku) => {
        const item = bagItems[sku.id];
        const quantity = item.quantity;

        return prevValue + quantity * sku.price.value;
      }, 0);

    const totalPriceObj = {
      value: totalValue,
      currency: skus[0].price.currency,
    };

    return formatMoneyForLocale(locale, totalPriceObj, { subunits: true });
  };

  const signUpOrSignIn = () => {
    const businessLine = Object.values(bagItems)[0].businessLine;

    redirectToSignup({
      skus: inStockSkus()?.map(sku => {
        return {
          id: sku.id,
          quantity: bagItems[sku.id]?.quantity,
          source: bagItems[sku.id]?.source || sourceType.UNGATED,
          subSource: bagItems[sku.id]?.sub_source,
        };
      }),
      productBusinessLine: businessLine,
      orderType: 'ungated_shopping_bag',
      orderSubtotal: formattedSubtotal(),
    });
  };

  const reviewOrder = () => {
    signUpOrSignIn();
    trackReviewOrderClick();
  };

  const renderOrderSummary = () => (
    <Box as="aside" styleName="right-column">
      <Stack spacing={0.5} mb={2} data-testid="order-summary">
        <Text height="standard" family="medium" scale="4">
          <Box styleName="order-summary-line">
            <Box>Subtotal</Box>
            <Box>{formattedSubtotal()}</Box>
          </Box>
        </Text>

        <Text height="standard" family="regular" scale="3">
          <Box styleName="order-summary-line">
            <Box>Shipping</Box>
            <Box>FREE</Box>
          </Box>
        </Text>

        {canSeeShippingPromises && (
          <Text height="tall" family="regular" scale="2" color="gray-45">
            <Box>
              <Box>Est. 4&ndash;8 business days</Box>
            </Box>
          </Text>
        )}

        {isMobileScreenSize && showSecurePaymentMessage && (
          <>
            <Divider variant="subtle" my={0.75} />
            <Text>
              <Box styleName="discount-credit-copy" pb={{ md: 0.75, sm: 0.75 }}>
                {isMobileScreenSize ? (
                  <IconGiftCards16 styleName="icon" title="Gift Card Icon" />
                ) : (
                  <IconGiftCards24 styleName="icon" title="Gift Card Icon" />
                )}

                <Text setting="body-small" ml={0.5}>
                  Discounts and credits will be applied at checkout.
                </Text>
              </Box>
              <Box styleName="secure-payments-copy" pb={{ md: 0.75, sm: 0.75 }}>
                <Box>
                  {isMobileScreenSize ? (
                    <IconLockLocked16 styleName="icon" title="Secure Icon" />
                  ) : (
                    <IconLockLocked24 styleName="icon" title="Secure Icon" />
                  )}
                </Box>
                <Text setting="body-small" ml={0.5}>
                  Trusted and secure payments
                </Text>
              </Box>
            </Text>
          </>
        )}
      </Stack>
      <StickyCTA
        onClick={reviewOrder}
        aboveButtonContent={() => <ShippingDetails mobile />}
        disabled={skuQuantityExceedsAvailable() || businessLineMismatch}
      >
        Check out
      </StickyCTA>
      <Box display={{ sm: 'none', md: 'block' }}>
        <ShippingDetails />
        {!isMobileScreenSize && showSecurePaymentMessage && (
          <Text>
            <Box styleName="discount-credit-copy" pb={{ md: 0.75, sm: 0.75 }}>
              {isMobileScreenSize ? (
                <IconGiftCards16 styleName="icon" title="Gift Card Icon" />
              ) : (
                <IconGiftCards24 styleName="icon" title="Gift Card Icon" />
              )}

              <Text setting="body-small" ml={0.5}>
                Discounts and credits will be applied at checkout.
              </Text>
            </Box>
            <Box styleName="secure-payments-copy" pb={{ md: 0.75, sm: 0.75 }}>
              <Box>
                {isMobileScreenSize ? (
                  <IconLockLocked16 styleName="icon" title="Secure Icon" />
                ) : (
                  <IconLockLocked24 styleName="icon" title="Secure Icon" />
                )}
              </Box>
              <Text setting="body-small" ml={0.5}>
                Trusted and secure payments
              </Text>
            </Box>
          </Text>
        )}
      </Box>
    </Box>
  );

  const renderBusinessLineMismatchBanner = () => (
    <Banner
      variant="info-border"
      data-testid="line-of-business-mismatch-banner"
      onClose={() => setBusinessLineMismatch(false)}
      headline="Our apologies! Men’s and Women’s styles need to be purchased through separate accounts."
    >
      To buy the {formatBusinessLine(bagBusinessLine)} styles please log out and
      create a new account with a different email address. Sorry for the hassle.
    </Banner>
  );

  const renderBag = () => (
    <div styleName="shopping-bag-body">
      <Box as="main" styleName="bag-main">
        {businessLineMismatch && renderBusinessLineMismatchBanner()}
        <Box as="header" styleName="bag-title" aria-level="1">
          <Text setting="title-xlarge" mb={0.625}>
            Your bag
          </Text>
          <Text setting="body-medium" mb={0.625}>
            Get the items in your bag before they sell out.
          </Text>
        </Box>
        <Box as="article" styleName="left-column">
          <Stack divider="item">{products?.map(renderBagItem)}</Stack>
        </Box>
        {isAnySkuInStock() && renderOrderSummary()}
      </Box>
      <Toast
        isOpen={displayBagLimitToast}
        onClose={() => setDisplayBagLimitToast(false)}
        startContent={<IconError16 purpose="decorative" />}
      >
        Your bag is full. Remove items to add more.
      </Toast>
    </div>
  );

  const ciCookie = Cookies.get('SDCIT1Exp');

  const showCreditIncentivesBanner = () => {
    if (bagIsEmpty || !ciCookie || !creditIncentivesEligible) {
      return false;
    }
    const expiryDate = new Date(ciCookie);

    return expiryDate.getTime() >= new Date().getTime();
  };

  const bagCount = getTotalBagQuantity(bagItems);

  return (
    <div>
      <BagInventoryFailureModal />
      <BagHeader bagCount={bagCount} />
      <ErrorBoundary>
        {showCreditIncentivesBanner() && (
          <CreditIncentivesBanner onShoppingBag />
        )}
        {bagIsEmpty ? renderEmptyBag() : renderBag()}
      </ErrorBoundary>
      {!isLoggedIn && bagIsEmpty && <BagFooter />}
    </div>
  );
};

ShoppingBag.propTypes = {
  isLoggedIn: PropTypes.bool,
  canSeeShippingPromises: PropTypes.bool,
};

ShoppingBag.defaultProps = {
  isLoggedIn: false,
  canSeeShippingPromises: true, // currently turned on for 100% traffic but keeping a feature flag in case product wants to turn off at any point
};

export default ShoppingBag;
